<!--
 * @Description: 核心指标 - 平台数据看板
-->
<template>
  <div class="core-metrics">
    <Search v-bind="filterAttrs" v-on="filterEvent" />

    <div v-loading="loading" class="metrics-body">
      <div class="summary-grid">
        <div
          v-for="item in summaryList"
          :key="item.key"
          class="summary-card"
          :style="{ borderTopColor: item.color }"
        >
          <div class="summary-card__title">{{ item.title }}</div>
          <div class="summary-card__amount">¥ {{ formatAmount(item.total) }}</div>
          <div class="summary-card__desc">{{ item.desc }}</div>
        </div>
      </div>

      <div class="section">
        <div class="section__header">
          <h3>渠道维度</h3>
          <span class="section__tip">按渠道汇总，金额单位：元</span>
        </div>
        <el-row :gutter="16">
          <el-col
            v-for="panel in channelPanels"
            :key="panel.key"
            :xs="24"
            :sm="24"
            :md="12"
          >
            <metric-panel v-bind="panel" />
          </el-col>
        </el-row>
      </div>

      <div class="section">
        <div class="section__header">
          <h3>商户维度</h3>
          <span class="section__tip">按商户汇总（仅已核销券码），金额单位：元</span>
        </div>
        <el-row :gutter="16">
          <el-col
            v-for="panel in merchantPanels"
            :key="panel.key"
            :xs="24"
            :sm="24"
            :md="12"
          >
            <metric-panel v-bind="panel" />
          </el-col>
        </el-row>
      </div>
    </div>
  </div>
</template>

<script>
import Search from "@/components/Search/index.vue";
import MetricPanel from "./MetricPanel.vue";
import { coreDisplay } from "@/api/bill";

const METRIC_DEFS = {
  channelAmount: {
    title: "渠道下单券码金额",
    desc: "选定时间内，各渠道下单的券码面额合计",
    nameKey: "channelName",
    fallbackKey: "channelId",
    color: "#1890ff",
    group: "channel",
  },
  channelUnPaidAmount: {
    title: "渠道应付金额",
    desc: "未核销、已核销状态下，各渠道应付金额合计",
    nameKey: "channelName",
    fallbackKey: "channelId",
    color: "#13c2c2",
    group: "channel",
  },
  channelEarnAmount: {
    title: "平台核销利润",
    desc: "各渠道已核销券码产生的平台利润合计",
    nameKey: "channelName",
    fallbackKey: "channelId",
    color: "#52c41a",
    group: "channel",
  },
  channelExpireUnPaidAmount: {
    title: "平台券码沉淀",
    desc: "已过期且未核销的券码，各渠道沉淀金额合计",
    nameKey: "channelName",
    fallbackKey: "channelId",
    color: "#fa8c16",
    group: "channel",
  },
  merchantCouponAmount: {
    title: "已核销券码金额",
    desc: "各商户已核销券码的面额合计",
    nameKey: "merchantName",
    fallbackKey: "merchantId",
    color: "#722ed1",
    group: "merchant",
  },
  merchantPaidAmount: {
    title: "已核销商户结算",
    desc: "各商户已核销券码对应的商户结算金额合计",
    nameKey: "merchantName",
    fallbackKey: "merchantId",
    color: "#eb2f96",
    group: "merchant",
  },
};

export default {
  name: "coreMetrics",
  components: {
    Search,
    MetricPanel,
  },
  data() {
    return {
      loading: false,
      params: {
        startDate: "",
        endDate: "",
      },
      metricData: {},
      filterButtonText: [],
      filterOptions: [
        { format: "mulDate" },
        { type: "button" },
      ],
    };
  },
  computed: {
    filterAttrs() {
      return {
        filterButtonText: this.filterButtonText,
        filterOptions: this.filterOptions,
      };
    },
    filterEvent() {
      return {
        changeDate: this.changeDate,
        clickSearch: this.clickSearch,
      };
    },
    summaryList() {
      return Object.keys(METRIC_DEFS).map((key) => {
        const def = METRIC_DEFS[key];
        const rows = this.normalizeRows(key, this.metricData[key]);
        return {
          key,
          title: def.title,
          desc: def.desc,
          color: def.color,
          total: this.sumRows(rows),
        };
      });
    },
    channelPanels() {
      return this.buildPanels("channel");
    },
    merchantPanels() {
      return this.buildPanels("merchant");
    },
  },
  mounted() {
    this.loadMetrics();
  },
  methods: {
    buildPanels(group) {
      return Object.keys(METRIC_DEFS)
        .filter((key) => METRIC_DEFS[key].group === group)
        .map((key) => {
          const def = METRIC_DEFS[key];
          const rows = this.normalizeRows(key, this.metricData[key]);
          const total = this.sumRows(rows);
          return {
            key,
            title: def.title,
            desc: def.desc,
            color: def.color,
            rows,
            total,
            nameLabel: group === "channel" ? "渠道" : "商户",
          };
        });
    },
    normalizeRows(key, list) {
      const def = METRIC_DEFS[key];
      return (list || [])
        .map((item) => ({
          name:
            item[def.nameKey] ||
            item[def.fallbackKey] ||
            "-",
          amount: (item.amount || 0) / 100,
        }))
        .sort((a, b) => b.amount - a.amount);
    },
    sumRows(rows) {
      return rows.reduce((sum, row) => sum + row.amount, 0);
    },
    formatAmount(value) {
      const num = Number(value) || 0;
      return num.toLocaleString("zh-CN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    changeDate(val) {
      this.params.startDate = val[0];
      this.params.endDate = val[1];
    },
    clickSearch() {
      this.loadMetrics();
    },
    async loadMetrics() {
      this.loading = true;
      try {
        const { data } = await coreDisplay({
          startDate: this.params.startDate,
          endDate: this.params.endDate,
        });
        this.metricData = data || {};
      } catch (error) {
        console.log(error);
        this.$message.error("核心指标加载失败");
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.core-metrics {
  .metrics-body {
    min-height: 200px;
  }

  .summary-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
    margin-bottom: 20px;

    @media (max-width: 1200px) {
      grid-template-columns: repeat(2, 1fr);
    }

    @media (max-width: 768px) {
      grid-template-columns: 1fr;
    }
  }

  .summary-card {
    background: #fff;
    border-radius: 8px;
    padding: 16px 18px;
    border-top: 3px solid #1890ff;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);

    &__title {
      font-size: 14px;
      font-weight: 600;
      color: #303133;
      margin-bottom: 8px;
    }

    &__amount {
      font-size: 24px;
      font-weight: 700;
      color: #1890ff;
      line-height: 1.3;
      margin-bottom: 8px;
    }

    &__desc {
      font-size: 12px;
      color: #909399;
      line-height: 1.5;
    }
  }

  .section {
    background: #fff;
    border-radius: 8px;
    padding: 16px 20px 20px;
    margin-bottom: 16px;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);

    &__header {
      display: flex;
      align-items: baseline;
      justify-content: space-between;
      margin-bottom: 16px;
      padding-bottom: 12px;
      border-bottom: 1px solid #f0f0f0;

      h3 {
        margin: 0;
        font-size: 16px;
        font-weight: 600;
        color: #303133;
      }
    }

    &__tip {
      font-size: 12px;
      color: #909399;
    }
  }
}
</style>
